import { memo } from "react";
import {
  SPECS,
  terminalLocal,
  type DeviceSpec,
} from "@/lib/sim/catalog";
import type { Comp } from "@/lib/sim/engine";

export interface DeviceViewProps {
  comp: Comp;
  selected: boolean;
  monitor: boolean;
  energized: boolean;
  coilPowered: boolean;
  running: boolean;
  /** اتجاه دوران المحرك الثلاثي القادم من engine.ts */
  motorDirection?: "forward" | "reverse";
  liveTerminals: Set<string>;
  /** أطراف مرتبطة فعلياً بسلك، حتى تظهر نقطة الربط كاملة باللون الأخضر. */
  wiredTerminals: Set<string>;
  pendingTerminal?: string | null;
  linkLabel?: string | null;
  actuated?: boolean;

  onBodyPointerDown: (
    e: React.PointerEvent,
    id: string
  ) => void;

  onTerminalPointerDown: (
    e: React.PointerEvent,
    id: string,
    t: string
  ) => void;

  onDeviceAction: (
    e: React.PointerEvent,
    id: string,
    phase: "down" | "up"
  ) => void;

  onOverloadControl: (
    e: React.PointerEvent,
    id: string,
    action: "stop" | "reset"
  ) => void;
}

const metal = "url(#gMetal)";

/* =========================================================
   SHARED BODY
   ========================================================= */

function Case({
  w,
  h,
  fill = "url(#gGrey)",
  r = 7,
  ribs = 0,
}: {
  w: number;
  h: number;
  fill?: string;
  r?: number;
  ribs?: number;
}) {
  return (
    <>
      <rect
        x={3}
        y={4}
        width={w}
        height={h}
        rx={r}
        fill="#000"
        opacity={0.25}
      />

      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        rx={r}
        fill={fill}
        stroke="var(--device-edge)"
        strokeWidth={1.3}
      />

      <rect
        x={2}
        y={2}
        width={Math.max(1, w - 4)}
        height={h * 0.18}
        rx={Math.max(1, r - 2)}
        fill="#fff"
        opacity={0.14}
      />

      {Array.from({
        length: ribs,
      }).map((_, i) => (
        <rect
          key={i}
          x={w * 0.12}
          y={h * 0.3 + i * 7}
          width={w * 0.76}
          height={2.3}
          rx={1}
          fill="#000"
          opacity={0.12}
        />
      ))}
    </>
  );
}

/* =========================================================
   LED
   ========================================================= */

function Led({
  x,
  y,
  on,
  color = "var(--led-on)",
  r = 6,
}: {
  x: number;
  y: number;
  on: boolean;
  color?: string;
  r?: number;
}) {
  return (
    <g>
      {on && (
        <>
          <circle
            cx={x}
            cy={y}
            r={r * 2.5}
            fill={color}
            opacity={0.17}
          />

          <circle
            cx={x}
            cy={y}
            r={r * 1.7}
            fill={color}
            opacity={0.2}
          />
        </>
      )}

      <circle
        cx={x}
        cy={y}
        r={r + 1}
        fill="#1b2025"
      />

      <circle
        cx={x}
        cy={y}
        r={r}
        fill={
          on
            ? color
            : "var(--led-off)"
        }
        stroke="#596169"
        strokeWidth={0.8}
      />

      <ellipse
        cx={x - r * 0.28}
        cy={y - r * 0.33}
        rx={r * 0.3}
        ry={r * 0.2}
        fill="#fff"
        opacity={on ? 0.7 : 0.2}
      />
    </g>
  );
}

/* =========================================================
   DARK NAME PLATE
   ========================================================= */

function Plate({
  x,
  y,
  w,
  h,
  children,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  children?: React.ReactNode;
}) {
  return (
    <g>
      <rect
        x={x + 1}
        y={y + 2}
        width={w}
        height={h}
        rx={3}
        fill="#000"
        opacity={0.25}
      />

      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={3}
        fill="url(#gDark)"
        stroke="#000"
        strokeOpacity={0.4}
      />

      <rect
        x={x + 2}
        y={y + 2}
        width={w - 4}
        height={h * 0.25}
        rx={2}
        fill="#fff"
        opacity={0.05}
      />

      {children}
    </g>
  );
}

/* =========================================================
   TERMINAL SCREW
   هذا البرغي الوحيد الخاص بنقطة التوصيل
   ========================================================= */

function TerminalWell({
  x,
  y,
  side,
  live,
}: {
  x: number;
  y: number;
  side: string;
  live: boolean;
}) {
  const vertical =
    side === "top" ||
    side === "bottom";

  const ww = vertical ? 17 : 19;
  const hh = vertical ? 20 : 17;

  const screwR = 5.4;

  return (
    <g>
      {/* ظل الحاضنة */}
      <rect
        x={x - ww / 2 + 2}
        y={y - hh / 2 + 2}
        width={ww}
        height={hh}
        rx={3}
        fill="#000"
        opacity={0.27}
      />

      {/* حاضنة الطرف */}
      <rect
        x={x - ww / 2}
        y={y - hh / 2}
        width={ww}
        height={hh}
        rx={3}
        fill="#353c42"
        stroke="#161b1f"
        strokeWidth={1}
      />

      {/* الجزء العلوي اللامع */}
      <rect
        x={x - ww / 2 + 2}
        y={y - hh / 2 + 2}
        width={ww - 4}
        height={4}
        rx={1.5}
        fill="#fff"
        opacity={0.08}
      />

      {live && (
        <circle
          cx={x}
          cy={y}
          r={10}
          fill="var(--led-on)"
          opacity={0.14}
        />
      )}

      {/* رأس البرغي */}
      <circle
        cx={x}
        cy={y}
        r={screwR + 1}
        fill="#424a51"
        stroke="#111"
        strokeWidth={0.8}
      />

      <circle
        cx={x}
        cy={y}
        r={screwR}
        fill={live ? "#9cb8a8" : metal}
        stroke="#535b61"
        strokeWidth={0.7}
      />

      {/* X داخل البرغي */}
      <line
        x1={x - 3.5}
        y1={y - 3.5}
        x2={x + 3.5}
        y2={y + 3.5}
        stroke="#42494f"
        strokeWidth={1.3}
        strokeLinecap="round"
      />

      <line
        x1={x + 3.5}
        y1={y - 3.5}
        x2={x - 3.5}
        y2={y + 3.5}
        stroke="#42494f"
        strokeWidth={1.3}
        strokeLinecap="round"
      />

      <ellipse
        cx={x - 1.7}
        cy={y - 2}
        rx={1.4}
        ry={1}
        fill="#fff"
        opacity={0.42}
      />
    </g>
  );
}

/* =========================================================
   DEVICE BODY
   ========================================================= */

function Body({
  spec,
  comp,
  monitor,
  energized,
  coilPowered,
  running,
  motorDirection,
  linkLabel,
  actuated,
}: {
  spec: DeviceSpec;
  comp: Comp;
  monitor: boolean;
  energized: boolean;
  coilPowered: boolean;
  running: boolean;
  motorDirection?: "forward" | "reverse";
  linkLabel?: string | null;
  actuated?: boolean;
}) {
  const { w, h } = spec;

  const tripped =
    !!comp.state.tripped;

  const closed =
    actuated ??
    !!comp.state.closed;

  const on =
    monitor &&
    energized;

  const coilOn =
    monitor &&
    coilPowered;

  switch (spec.body) {
    /* =====================================================
       CONTACTOR
       ===================================================== */

    case "contactor": {
      // الكونتكتر الثلاثي فقط: استخدم صورة Schneider TeSys LC1D25
      // المرسلة من المستخدم كما هي، بدون إعادة رسم أو تغيير الكونتكتر المساعد.
      if (spec.type === "contactor" || spec.type === "contactor_small") {
        const isFourPole = spec.type === "contactor_small";
        return (
          <>
            <image
              href={isFourPole ? "/contactor-lc1dt40.png" : "/contactor-lc1d25.png"}
              x={0}
              y={0}
              width={w}
              height={h}
              preserveAspectRatio="none"
              // الصورة نفسها تستقبل السحب حتى يمكن تحريك الكونتكتر بحرية.
              pointerEvents="visiblePainted"
            />

            {/* لمبة تشغيل حمراء أسفل كلمة Control. تظهر فقط عند تغذية ملف الكونتكتر. */}
            {coilOn && (
              <g pointerEvents="none">
                <circle cx={isFourPole ? 20 : 24} cy={isFourPole ? 216 : 184} r={11} fill="#ff1f1f" opacity={0.22} filter="url(#fLampGlow)" />
                <circle cx={isFourPole ? 20 : 24} cy={isFourPole ? 216 : 184} r={6.2} fill="#ff1f1f" stroke="#8b0000" strokeWidth={1.2} />
                <circle cx={isFourPole ? 18.2 : 22.2} cy={isFourPole ? 214.2 : 182.2} r={1.7} fill="#fff" opacity={0.8} />
              </g>
            )}
          </>
        );
      }

      const sh =
        h * 0.19;

      const small =
        spec.type ===
        "contactor_small";

      return (
        <>
          {/* ظل كامل */}
          <rect
            x={4}
            y={5}
            width={w}
            height={h}
            rx={7}
            fill="#000"
            opacity={0.3}
          />

          {/* حجرة الأطراف العليا */}
          <rect
            x={-2}
            y={-2}
            width={w + 4}
            height={sh + 4}
            rx={5}
            fill="url(#gShroud)"
            stroke="#252b30"
            strokeWidth={1}
          />

          <rect
            x={0}
            y={0}
            width={w}
            height={5}
            rx={3}
            fill="#fff"
            opacity={0.12}
          />

          {/* حجرة الأطراف السفلى */}
          <rect
            x={-2}
            y={h - sh - 2}
            width={w + 4}
            height={sh + 4}
            rx={5}
            fill="url(#gShroud)"
            stroke="#252b30"
            strokeWidth={1}
          />

          {/* جوانب الكونتكتر */}
          <rect
            x={-2}
            y={sh}
            width={w * 0.13}
            height={h - sh * 2}
            rx={3}
            fill="url(#gShroud)"
          />

          <rect
            x={
              w -
              w * 0.13 +
              2
            }
            y={sh}
            width={w * 0.13}
            height={h - sh * 2}
            rx={3}
            fill="url(#gShroud)"
          />

          {/* الجسم الأسود */}
          <rect
            x={w * 0.11}
            y={sh}
            width={w * 0.78}
            height={
              h -
              sh * 2
            }
            rx={5}
            fill="url(#gCharcoal)"
            stroke="#101418"
            strokeWidth={1}
          />

          {/* شريط أخضر */}
          <rect
            x={w * 0.13}
            y={sh + 5}
            width={w * 0.74}
            height={h * 0.09}
            rx={2}
            fill="url(#gTeSys)"
          />

          {/* فقط TeSys — تم حذف Schneider */}
          <text
            x={w / 2}
            y={
              sh +
              h * 0.071
            }
            textAnchor="middle"
            fill="#ffffff"
            fontSize={8}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#14743a",
              strokeWidth: 0.8,
            }}
          >
            TeSys
          </text>

          {/* لوحة الواجهة */}
          <rect
            x={w * 0.145}
            y={
              sh +
              h * 0.145
            }
            width={w * 0.71}
            height={
              h -
              sh * 2 -
              h * 0.16
            }
            rx={4}
            fill="#171b1f"
            stroke="#080a0c"
            strokeWidth={0.8}
          />

          {/* الموديل */}
          <text
            x={w * 0.19}
            y={
              sh +
              h * 0.29
            }
            fill="#ffffff"
            fontSize={9}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#111",
              strokeWidth: 1.5,
            }}
          >
            {small
              ? "CAD32"
              : "LC1D"}
          </text>

          {/* نافذة القلب المتحرك */}
          <rect
            x={w * 0.39}
            y={h * 0.43}
            width={w * 0.25}
            height={h * 0.2}
            rx={3}
            fill="#07090b"
            stroke="#626d76"
            strokeWidth={1}
          />

          <rect
            x={w * 0.425}
            y={
              h *
              (coilOn
                ? 0.53
                : 0.46)
            }
            width={w * 0.18}
            height={h * 0.075}
            rx={2}
            fill={
              coilOn
                ? "#4484ff"
                : "#254992"
            }
            style={{
              transition:
                "y 110ms ease-out",
            }}
          />

          <rect
            x={w * 0.445}
            y={
              h *
              (coilOn
                ? 0.542
                : 0.472)
            }
            width={w * 0.13}
            height={h * 0.015}
            rx={1}
            fill="#fff"
            opacity={0.22}
          />

          {/* ON/OFF */}
          <rect
            x={w * 0.675}
            y={h * 0.47}
            width={w * 0.16}
            height={h * 0.11}
            rx={2}
            fill={
              on
                ? "#188d4a"
                : "#31373c"
            }
            stroke="#080a0c"
          />

          <text
            x={w * 0.755}
            y={h * 0.545}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={7}
            fontWeight={900}
          >
            {on
              ? "ON"
              : "OFF"}
          </text>

          {/* لمبة الملف */}
          <Led
            x={w * 0.2}
            y={h * 0.57}
            on={coilOn}
            r={4.3}
          />

          <text
            x={w * 0.2}
            y={h * 0.66}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.5}
            fontWeight={800}
          >
            COIL
          </text>
        </>
      );
    }

    /* =====================================================
       RELAY / TIMER
       ===================================================== */

    case "relay":
    case "timer": {
      if (spec.type === "relay") {
        return (
          <>
            <image href="/relay-rxm.png" x={0} y={0} width={w} height={h} preserveAspectRatio="none" pointerEvents="visiblePainted" />
            {coilOn && (
              <g pointerEvents="none">
                {/* Illuminate the relay's ORIGINAL lamp symbol; no extra label/terminal artwork. */}
                <circle cx={w * 0.770} cy={h * 0.598} r={w * 0.026} fill="#ff2020" opacity={0.95} />
                <circle cx={w * 0.770} cy={h * 0.598} r={w * 0.050} fill="#ff2020" opacity={0.20} />
              </g>
            )}
          </>
        );
      }

      // تايمر PF083A: نفس الصورة تُستخدم للـON-Delay والـOFF-Delay.
      // الأطراف الفعلية موجودة على الصورة نفسها، لذلك لا نرسم جسم/أرقام إضافية.
      // للـ ON-Delay: مصباح ON يشتغل فور وصول الكهرباء، أما UP فلا يشتغل إلا بعد اكتمال الزمن.
      const timerDelayMs =
        comp.state.delay ??
        (spec.delay ?? 0) * 1000;
      const timerElapsedMs =
        comp.state.elapsed ?? 0;
      const timerUpOn =
        spec.timerMode === "on"
          ? coilOn && timerElapsedMs >= timerDelayMs
          : on;

      return (
        <>
          <image
            href="/timer-pf083a.png"
            x={0}
            y={0}
            width={w}
            height={h}
            preserveAspectRatio="none"
            pointerEvents="visiblePainted"
          />
{/* شاشة رقمية عصرية في وسط التايمر */}
<g pointerEvents="none">
  {/* الإطار الخارجي */}
  <rect
    x={w * 0.40}
    y={h * 0.420}
    width={w * 0.20}
    height={h * 0.10}
    rx={4}
    fill="#15191d"
    stroke="#2f353a"
    strokeWidth={1.5}
  />

  {/* داخل الشاشة */}
  <rect
    x={w * 0.40}
    y={h * 0.428}
    width={w * 0.20}
    height={h * 0.088}
    rx={2.5}
    fill="#06110d"
    stroke="#0d4f35"
    strokeWidth={1}
  />

  {/* الرقم */}
  <text
    x={w * 0.49}
    y={h * 0.485}
    textAnchor="middle"
    fill="#48ff9b"
    fontSize={w * 0.060}
    fontWeight={900}
    fontFamily="monospace"
    style={{
      filter: "drop-shadow(0 0 2px #48ff9b)",
    }}
  >
    {(Math.min(timerElapsedMs, timerDelayMs) / 1000).toFixed(1)}
  </text>

  {/* حرف الثانية */}
  <text
    x={w * 0.58}
    y={h * 0.50}
    textAnchor="middle"
    fill="#8affbd"
    fontSize={w * 0.035}
    fontWeight={800}
  >
    s
  </text>
</g>

          {/* مصباح ON: يشتغل مباشرة عند وصول التغذية إلى الكويل 2/7. */}
          {coilOn && (
            <g pointerEvents="none">
              <circle cx={w *0.373} cy={h * 0.328} r={w * 0.015} fill="#ff2020" opacity={0.70} />
              <circle cx={w * 0.373} cy={h * 0.328} r={w * 0.030} fill="#ff2020" opacity={0.30} />
            </g>
          )}

          {/* مصباح UP: في ON-Delay لا يضيء إلا بعد اكتمال الزمن. */}
          {timerUpOn && (
            <g pointerEvents="none">
              <circle cx={w * 0.621} cy={h * 0.328} r={w * 0.015} fill="#ff2020" opacity={0.70} />
              <circle cx={w * 0.618} cy={h * 0.328} r={w * 0.030} fill="#ff2020" opacity={0.30} />
            </g>
          )}
        </>
      );
    }

    /* =====================================================
       PUSH BUTTON
       ===================================================== */

    case "pb_green":
    case "pb_red":
    case "pb_black": {
      const pressed =
        !!comp.state.pressed;

      const cap =
        spec.body ===
        "pb_green"
          ? "url(#gCapGreen)"
          : spec.body ===
              "pb_red"
            ? "url(#gCapRed)"
            : "url(#gCapBlack)";

      const cx =
        w * 0.54;

      const cy =
        h * 0.45;

      const bezel =
        w * 0.34;

      return (
        <>
          <ellipse
            cx={cx}
            cy={h * 0.9}
            rx={w * 0.4}
            ry={h * 0.06}
            fill="#000"
            opacity={0.26}
          />

          <rect
            x={w * 0.03}
            y={h * 0.16}
            width={w * 0.33}
            height={h * 0.6}
            rx={4}
            fill="url(#gBlackTrim)"
            stroke="#080a0c"
          />

          <rect
            x={w * 0.34}
            y={h * 0.26}
            width={w * 0.2}
            height={h * 0.38}
            rx={4}
            fill="url(#gBlackTrim)"
          />

          {Array.from({
            length: 7,
          }).map((_, i) => (
            <line
              key={i}
              x1={
                w * 0.35 +
                i *
                  w *
                  0.026
              }
              y1={h * 0.28}
              x2={
                w * 0.35 +
                i *
                  w *
                  0.026
              }
              y2={h * 0.62}
              stroke="#000"
              strokeOpacity={0.45}
            />
          ))}

          <circle
            cx={cx}
            cy={cy}
            r={bezel}
            fill="url(#gChrome)"
            stroke="#596168"
          />

          <circle
            cx={cx}
            cy={cy}
            r={bezel * 0.85}
            fill="#70787f"
          />

          <circle
            cx={cx}
            cy={
              cy +
              (pressed
                ? 2
                : 0)
            }
            r={
              bezel *
              (pressed
                ? 0.72
                : 0.78)
            }
            fill={cap}
            stroke="#111"
            strokeOpacity={0.45}
            style={{
              transition:
                "all 80ms ease-out",
            }}
          />

          <ellipse
            cx={
              cx -
              bezel * 0.18
            }
            cy={
              cy -
              bezel * 0.28 +
              (pressed
                ? 2
                : 0)
            }
            rx={bezel * 0.34}
            ry={bezel * 0.19}
            fill="#fff"
            opacity={
              pressed
                ? 0.1
                : 0.27
            }
          />

          <text
            x={w / 2}
            y={h * 0.95}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.5}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#111",
              strokeWidth: 1.5,
            }}
          >
            {spec.type ===
            "pb_stop"
              ? "STOP · NC"
              : spec.type ===
                  "pb_start"
                ? "START · NO"
                : spec.type ===
                    "pb_nc"
                  ? "NC"
                  : "NO"}
          </text>
        </>
      );
    }

    /* =====================================================
       EMERGENCY STOP
       ===================================================== */

    case "estop": {
      const pressed =
        closed;

      const cx =
        w / 2;

      const cy =
        h * 0.42;

      const r =
        w * 0.42;

      const drop =
        pressed ? 3 : 0;

      return (
        <>
          <ellipse
            cx={cx}
            cy={h * 0.93}
            rx={w * 0.36}
            ry={h * 0.05}
            fill="#000"
            opacity={0.3}
          />

          <rect
            x={w * 0.15}
            y={h * 0.62}
            width={w * 0.7}
            height={h * 0.24}
            rx={4}
            fill="url(#gBlackTrim)"
          />

          <rect
            x={w * 0.3}
            y={
              h * 0.5 +
              drop
            }
            width={w * 0.4}
            height={h * 0.16}
            rx={4}
            fill="url(#gBlackTrim)"
          />

          {Array.from({
            length: 9,
          }).map((_, i) => (
            <line
              key={i}
              x1={
                w * 0.31 +
                i *
                  w *
                  0.045
              }
              y1={
                h * 0.51 +
                drop
              }
              x2={
                w * 0.31 +
                i *
                  w *
                  0.045
              }
              y2={
                h *
                  0.655 +
                drop
              }
              stroke="#000"
              strokeOpacity={0.45}
            />
          ))}

          <rect
            x={w * 0.34}
            y={cy + drop}
            width={w * 0.32}
            height={h * 0.16}
            rx={3}
            fill="url(#gEstopRed)"
          />

          <ellipse
            cx={cx}
            cy={cy + drop}
            rx={r}
            ry={r * 0.74}
            fill="url(#gEstopRed)"
            stroke="#650c08"
          />

          <ellipse
            cx={cx}
            cy={
              cy -
              r * 0.12 +
              drop
            }
            rx={r * 0.88}
            ry={r * 0.58}
            fill="#fff"
            opacity={0.06}
          />

          <text
            x={cx}
            y={h * 0.97}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.3}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#111",
              strokeWidth: 1.5,
            }}
          >
            {pressed
              ? "LOCKED · NC"
              : "EMERGENCY · NC"}
          </text>
        </>
      );
    }

    /* =====================================================
       SELECTOR
       ===================================================== */

    case "selector": {
      const positions =
        spec.positions ??
        ["OFF", "ON"];

      const pos =
        Math.max(
          0,
          Math.min(
            comp.state.pos ??
              0,
            positions.length -
              1
          )
        );

      const angle =
        positions.length ===
        1
          ? 0
          : -45 +
            (90 /
              (positions.length -
                1)) *
              pos;

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            r={9}
          />

          <circle
            cx={w / 2}
            cy={h * 0.46}
            r={w * 0.3}
            fill={metal}
            stroke="#596168"
          />

          <circle
            cx={w / 2}
            cy={h * 0.46}
            r={w * 0.235}
            fill="#181d21"
          />

          {positions.map(
            (name, i) => {
              const a =
                ((-45 +
                  (positions.length ===
                  1
                    ? 0
                    : (90 /
                        (positions.length -
                          1)) *
                      i) -
                  90) *
                  Math.PI) /
                180;

              return (
                <g
                  key={`${name}-${i}`}
                >
                  <circle
                    cx={
                      w / 2 +
                      Math.cos(
                        a
                      ) *
                        w *
                        0.36
                    }
                    cy={
                      h * 0.46 +
                      Math.sin(
                        a
                      ) *
                        w *
                        0.36
                    }
                    r={2}
                    fill={
                      pos === i
                        ? "#f4ca43"
                        : "#7e878e"
                    }
                  />

                  <text
                    x={
                      w / 2 +
                      Math.cos(
                        a
                      ) *
                        w *
                        0.45
                    }
                    y={
                      h * 0.46 +
                      Math.sin(
                        a
                      ) *
                        w *
                        0.45 +
                      3
                    }
                    textAnchor="middle"
                    fill="#ffffff"
                    fontSize={5.5}
                    fontWeight={800}
                    style={{
                      paintOrder:
                        "stroke",
                      stroke:
                        "#111",
                      strokeWidth:
                        1.2,
                    }}
                  >
                    {name.length >
                    6
                      ? name.slice(
                          0,
                          6
                        )
                      : name}
                  </text>
                </g>
              );
            }
          )}

          <g
            transform={`rotate(${angle} ${w / 2} ${h * 0.46})`}
            style={{
              transition:
                "transform 120ms ease-out",
            }}
          >
            <rect
              x={w / 2 - 4}
              y={
                h * 0.46 -
                w * 0.26
              }
              width={8}
              height={w * 0.28}
              rx={4}
              fill={metal}
              stroke="#555d63"
            />
          </g>

          <text
            x={w / 2}
            y={h * 0.94}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.5}
            fontWeight={800}
          >
            {positions[pos]}
          </text>
        </>
      );
    }

    /* =====================================================
       LIMIT / AUX
       ===================================================== */

    case "limit":
    case "aux": {
      const isLimit =
        spec.body ===
        "limit";

      // أوكسيلاري LADN13 الحقيقي المرسل من المستخدم، بدون خلفية.
      if (spec.type === "aux_block") {
        return (
          <>
            <image
              href="/auxiliary-ladn13.png"
              x={0}
              y={0}
              width={w}
              height={h}
              preserveAspectRatio="none"
              pointerEvents="visiblePainted"
            />
            {closed && (
              <g pointerEvents="none">
                <circle cx={w * 0.50} cy={h * 0.50} r={w * 0.045} fill="#ff2020" opacity={0.95} />
                <circle cx={w * 0.50} cy={h * 0.50} r={w * 0.075} fill="#ff2020" opacity={0.20} />
              </g>
            )}
          </>
        );
      }

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gGrey)"
            ribs={2}
          />

          {isLimit && (
            <>
              <rect
                x={w * 0.08}
                y={h * 0.13}
                width={w * 0.2}
                height={h * 0.16}
                rx={2}
                fill={metal}
                stroke="#535c63"
              />

              <line
                x1={w * 0.18}
                y1={h * 0.13}
                x2={
                  w *
                  (closed
                    ? 0.42
                    : 0.3)
                }
                y2={
                  h *
                  (closed
                    ? 0.02
                    : -0.06)
                }
                stroke="#c1c8cd"
                strokeWidth={4}
                strokeLinecap="round"
                style={{
                  transition:
                    "all 100ms ease-out",
                }}
              />
            </>
          )}

          <Plate
            x={10}
            y={h * 0.4}
            w={w - 20}
            h={h * 0.34}
          >
            <text
              x={w / 2}
              y={h * 0.55}
              textAnchor="middle"
              fill="#ffffff"
              fontSize={7}
              fontWeight={900}
            >
              {isLimit
                ? "LIMIT SWITCH"
                : linkLabel
                  ? "AUX CONTACT"
                  : "AUX CONTACT"}
            </text>

            <text
              x={w / 2}
              y={h * 0.68}
              textAnchor="middle"
              fill="#ffffff"
              fontSize={6}
              fontWeight={700}
            >
              {closed
                ? "ACTUATED"
                : "REST"}
            </text>
          </Plate>
        </>
      );
    }

    /* =====================================================
       MCB / MCCB / RCCB
       ===================================================== */

    case "mcb":
    case "mccb":
    case "rccb": {
      const lever =
        closed;

      const dark =
        spec.body ===
        "mccb";

      const poles =
        Math.max(
          1,
          spec.terminals.filter(
            (t) =>
              t.side ===
              "top"
          ).length
        );

      const sh =
        h * 0.17;

      const leverY =
        tripped
          ? h * 0.5
          : lever
            ? h * 0.455
            : h * 0.56;

      return (
        <>
          <rect
            x={3}
            y={4}
            width={w}
            height={h}
            rx={5}
            fill="#000"
            opacity={0.27}
          />

          <rect
            x={0}
            y={0}
            width={w}
            height={h}
            rx={5}
            fill={
              dark
                ? "url(#gCharcoal)"
                : "url(#gWhite)"
            }
            stroke="#60676c"
            strokeWidth={0.8}
          />

          {Array.from({
            length:
              poles - 1,
          }).map((_, i) => (
            <line
              key={i}
              x1={
                ((i + 1) *
                  w) /
                poles
              }
              y1={0}
              x2={
                ((i + 1) *
                  w) /
                poles
              }
              y2={h}
              stroke={
                dark
                  ? "#111"
                  : "#c1c4c3"
              }
              strokeOpacity={0.5}
            />
          ))}

          {dark ? (
            <>
              <rect
                x={w * 0.1}
                y={h * 0.28}
                width={w * 0.8}
                height={h * 0.43}
                rx={4}
                fill="#353c42"
              />

              <rect
                x={w * 0.29}
                y={h * 0.3}
                width={w * 0.42}
                height={h * 0.08}
                rx={2}
                fill="#c8102e"
              />

              <text
                x={w / 2}
                y={h * 0.36}
                textAnchor="middle"
                fill="#ffffff"
                fontSize={6.5}
                fontWeight={900}
              >
                ABB SACE
              </text>

              <rect
                x={w * 0.3}
                y={h * 0.41}
                width={w * 0.4}
                height={h * 0.27}
                rx={3}
                fill="#121619"
              />

              <rect
                x={w * 0.35}
                y={
                  leverY -
                  h * 0.05
                }
                width={w * 0.3}
                height={h * 0.13}
                rx={3}
                fill="url(#gHandle)"
                stroke="#6d7479"
                style={{
                  transition:
                    "y 120ms ease-out",
                }}
              />

              <text
                x={w * 0.23}
                y={h * 0.47}
                textAnchor="middle"
                fill="#ffffff"
                fontSize={7}
                fontWeight={900}
              >
                I
              </text>

              <text
                x={w * 0.23}
                y={h * 0.65}
                textAnchor="middle"
                fill="#ffffff"
                fontSize={7}
                fontWeight={900}
              >
                O
              </text>

              <text
                x={w / 2}
                y={h * 0.79}
                textAnchor="middle"
                fill="#ffffff"
                fontSize={6}
                fontWeight={800}
              >
                {tripped
                  ? "TRIP"
                  : `${poles}P MCCB`}
              </text>
            </>
          ) : (
            <>
              <rect
                x={0}
                y={h * 0.26}
                width={w}
                height={h * 0.05}
                fill="#d31e27"
              />

              <text
                x={w * 0.12}
                y={h * 0.3}
                fill="#ffffff"
                fontSize={6.5}
                fontWeight={900}
              >
                ABB
              </text>

              <text
                x={w / 2}
                y={h * 0.4}
                textAnchor="middle"
                fill="#24292d"
                fontSize={6}
                fontWeight={800}
              >
                {spec.body ===
                "rccb"
                  ? spec.type ===
                    "rcbo"
                    ? "RCBO · 30mA"
                    : "RCCB · 30mA"
                  : `C16 · ${poles}P`}
              </text>

              {Array.from({
                length:
                  poles,
              }).map((_, i) => {
                const cx =
                  (i +
                    0.5) *
                  (w /
                    poles);

                const bw =
                  Math.min(
                    w /
                      poles -
                      5,
                    34
                  );

                return (
                  <g key={i}>
                    <rect
                      x={
                        cx -
                        bw /
                          2
                      }
                      y={
                        h *
                        0.46
                      }
                      width={bw}
                      height={
                        h *
                        0.18
                      }
                      rx={2}
                      fill="#e5e6e2"
                      stroke="#c2c4c0"
                    />

                    <rect
                      x={
                        cx -
                        bw /
                          2 +
                        2
                      }
                      y={
                        tripped
                          ? h *
                            0.51
                          : lever
                            ? h *
                              0.465
                            : h *
                              0.55
                      }
                      width={
                        bw -
                        4
                      }
                      height={
                        h *
                        0.085
                      }
                      rx={2}
                      fill={
                        tripped
                          ? "#d99832"
                          : "#25292c"
                      }
                      stroke="#111"
                      style={{
                        transition:
                          "y 120ms ease-out",
                      }}
                    />
                  </g>
                );
              })}

              <text
                x={w / 2}
                y={h * 0.72}
                textAnchor="middle"
                fill="#23282c"
                fontSize={6.5}
                fontWeight={900}
              >
                {tripped
                  ? "TRIP"
                  : lever
                    ? "I · ON"
                    : "O · OFF"}
              </text>

              {spec.body ===
                "rccb" && (
                <>
                  <rect
                    x={w * 0.7}
                    y={h * 0.45}
                    width={w * 0.16}
                    height={h * 0.1}
                    rx={2}
                    fill="#2474bd"
                  />

                  <text
                    x={w * 0.78}
                    y={h * 0.52}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={6.5}
                    fontWeight={900}
                  >
                    T
                  </text>
                </>
              )}
            </>
          )}

          <rect
            x={0}
            y={h - 5}
            width={w}
            height={5}
            rx={2}
            fill="#000"
            opacity={0.14}
          />
        </>
      );
    }

    /* =====================================================
       OVERLOAD
       ===================================================== */

    case "overload":
      return (
        <>
          <image
            href="/overload-lrd06.png"
            x={0}
            y={0}
            width={w}
            height={h}
            preserveAspectRatio="none"
            pointerEvents="visiblePainted"
          />

          {/* مصباح Trip أحمر: يضيء فقط عندما يكون الأوفرلود مفصولاً. */}
          {tripped && (
            <g pointerEvents="none">
              <circle cx={119} cy={116} r={10.5} fill="#ff1f1f" opacity={0.22} filter="url(#fLampGlow)" />
              <circle cx={119} cy={116} r={5.8} fill="#ff1f1f" stroke="#8b0000" strokeWidth={1.1} />
              <circle cx={117.4} cy={114.4} r={1.5} fill="#fff" opacity={0.82} />
            </g>
          )}
        </>
      );

    /* =====================================================
       FUSE
       ===================================================== */

    case "fuse":
      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gGrey)"
            r={4}
          />

          <rect
            x={w * 0.22}
            y={h * 0.16}
            width={w * 0.56}
            height={h * 0.68}
            rx={4}
            fill="#11161a"
            opacity={0.6}
          />

          <rect
            x={w * 0.28}
            y={h * 0.2}
            width={w * 0.44}
            height={h * 0.14}
            rx={2}
            fill={metal}
          />

          <rect
            x={w * 0.28}
            y={h * 0.66}
            width={w * 0.44}
            height={h * 0.14}
            rx={2}
            fill={metal}
          />

          <rect
            x={w * 0.31}
            y={h * 0.34}
            width={w * 0.38}
            height={h * 0.32}
            rx={3}
            fill={
              tripped
                ? "#b84135"
                : "#edeadc"
            }
            stroke="#85847d"
          />

          {!tripped && (
            <path
              d={`M ${w * 0.4} ${h * 0.38} C ${w * 0.57} ${h * 0.44}, ${w * 0.43} ${h * 0.56}, ${w * 0.6} ${h * 0.62}`}
              fill="none"
              stroke="#986128"
              strokeWidth={1.8}
            />
          )}

          <text
            x={w / 2}
            y={h * 0.93}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.5}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#111",
              strokeWidth: 1.4,
            }}
          >
            {tripped
              ? "BLOWN"
              : "gG 16A"}
          </text>
        </>
      );

    /* =====================================================
       MONITOR / VOLTMETER / AMMETER
       ===================================================== */

    case "monitor": {
      const type =
        String(spec.type);

      const isVolt =
        type ===
        "voltmeter";

      const isAmp =
        type ===
        "ammeter";

      const isMulti =
        type ===
        "multimeter_panel";

      if (
        isVolt ||
        isAmp ||
        isMulti
      ) {
        const state =
          comp.state as typeof comp.state & {
            voltage?: number;
            current?: number;
            reading?: number;
          };

        const reading =
          isVolt
            ? state.voltage ??
              state.reading
            : isAmp
              ? state.current ??
                state.reading
              : state.reading;

        let display =
          "0";

        if (
          monitor &&
          typeof reading ===
            "number" &&
          Number.isFinite(
            reading
          )
        ) {
          display =
            reading.toFixed(
              isAmp ? 1 : 0
            );
        } else if (
          monitor &&
          energized
        ) {
          display = "---";
        }

        return (
          <>
            <Case
              w={w}
              h={h}
              fill="url(#gDark)"
            />

            <rect
              x={w * 0.1}
              y={h * 0.15}
              width={w * 0.8}
              height={h * 0.44}
              rx={4}
              fill="#05160c"
              stroke="#111"
            />

            <rect
              x={w * 0.12}
              y={h * 0.17}
              width={w * 0.76}
              height={h * 0.08}
              rx={2}
              fill="#fff"
              opacity={0.04}
            />

            <text
              x={w / 2}
              y={h * 0.42}
              className="dev-screen"
              textAnchor="middle"
              style={{
                fontSize: 18,
              }}
            >
              {display}
            </text>

            <text
              x={w * 0.8}
              y={h * 0.51}
              textAnchor="middle"
              fill="#9ff4b5"
              fontSize={7}
              fontWeight={900}
            >
              {isVolt
                ? "V"
                : isAmp
                  ? "A"
                  : ""}
            </text>

            <Led
              x={w * 0.2}
              y={h * 0.69}
              on={
                monitor &&
                energized
              }
              r={4}
            />

            <text
              x={w / 2}
              y={h * 0.87}
              textAnchor="middle"
              fill="#ffffff"
              fontSize={6.5}
              fontWeight={900}
            >
              {isVolt
                ? "VOLTMETER"
                : isAmp
                  ? "AMMETER"
                  : "MULTI METER"}
            </text>
          </>
        );
      }

      const isPhaseFailure =
        type === "phase_failure";

      const isVoltageMonitor =
        type === "voltage_monitor";

      if (
        isPhaseFailure ||
        isVoltageMonitor
      ) {
        /*
          مصابيح الحماية المربعة — التحكم اليدوي من هنا:

          x موجب = يمين | x سالب = يسار
          y موجب = جوه   | y سالب = فوك
          width  = عرض المصباح
          height = ارتفاع المصباح

          ON  = أحمر ويعمل فور وصول مصدر الكهرباء إلى الجهاز.
          OUT = أخضر ويعمل فقط بعد اكتمال زمن الحماية والحالة سليمة.
        */
        const protectionLampAdjust = isPhaseFailure
          ? {
              on: { x: 0, y: 0, width: 11, height: 4.8 },
              out: { x: 0, y: 0, width: 11, height: 4.8 },
            }
          : {
              on: { x: 0, y: 0, width: 11, height: 4.8 },
              out: { x: 0, y: 0, width: 11, height: 4.8 },
            };

        // مراكز نافذتي ON وOUT مأخوذة من الصورة الأصلية 1024×1536.
        const onCenterX = w * (406 / 1024);
        const onCenterY = h * (997 / 1536);
        const outCenterX = w * (628 / 1024);
        const outCenterY = h * (997 / 1536);

        const onRect = {
          x: onCenterX - protectionLampAdjust.on.width / 2 + protectionLampAdjust.on.x,
          y: onCenterY - protectionLampAdjust.on.height / 2 + protectionLampAdjust.on.y,
          width: protectionLampAdjust.on.width,
          height: protectionLampAdjust.on.height,
        };
        const outRect = {
          x: outCenterX - protectionLampAdjust.out.width / 2 + protectionLampAdjust.out.x,
          y: outCenterY - protectionLampAdjust.out.height / 2 + protectionLampAdjust.out.y,
          width: protectionLampAdjust.out.width,
          height: protectionLampAdjust.out.height,
        };

        const protectionState = comp.state as typeof comp.state & {
          protectionInputPowered?: boolean;
          protectionActive?: boolean;
        };

        const onLamp =
          monitor && !!protectionState.protectionInputPowered;
        const outLamp =
          monitor && !!protectionState.protectionActive;

        return (
          <>
            <image
              href={
                isPhaseFailure
                  ? "/phase-failure-skad04.png"
                  : "/voltage-monitor-skad0.png"
              }
              x={0}
              y={0}
              width={w}
              height={h}
              preserveAspectRatio="none"
              pointerEvents="visiblePainted"
            />

            {/* ON أحمر: يعمل فور وصول المصدر */}
            {onLamp && (
              <g pointerEvents="none">
                <rect
                  x={onRect.x - 2}
                  y={onRect.y - 2}
                  width={onRect.width + 4}
                  height={onRect.height + 4}
                  rx={1.8}
                  fill="#ff2424"
                  opacity={0.20}
                  filter="url(#fLampGlow)"
                />
                <rect
                  x={onRect.x}
                  y={onRect.y}
                  width={onRect.width}
                  height={onRect.height}
                  rx={1}
                  fill="#ff2424"
                  stroke="#8b0000"
                  strokeWidth={0.8}
                />
              </g>
            )}

            {/* OUT أخضر: يعمل بعد انتهاء الزمن والحالة سليمة */}
            {outLamp && (
              <g pointerEvents="none">
                <rect
                  x={outRect.x - 2}
                  y={outRect.y - 2}
                  width={outRect.width + 4}
                  height={outRect.height + 4}
                  rx={1.8}
                  fill="#23e66b"
                  opacity={0.20}
                  filter="url(#fLampGlow)"
                />
                <rect
                  x={outRect.x}
                  y={outRect.y}
                  width={outRect.width}
                  height={outRect.height}
                  rx={1}
                  fill="#21d866"
                  stroke="#087c36"
                  strokeWidth={0.8}
                />
              </g>
            )}
          </>
        );
      }

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            ribs={2}
          />

          <rect
            x={12}
            y={h * 0.18}
            width={w - 24}
            height={h * 0.3}
            rx={3}
            fill="#06150d"
            stroke="#111"
          />

          <text
            x={w / 2}
            y={h * 0.38}
            className="dev-screen"
            textAnchor="middle"
          >
            {tripped
              ? "FAULT"
              : "3-PH OK"}
          </text>

          {[
            "L1",
            "L2",
            "L3",
          ].map((name, i) => (
            <g key={name}>
              <Led
                x={
                  w *
                  (0.24 +
                    i *
                      0.26)
                }
                y={h * 0.59}
                on={
                  monitor &&
                  !tripped
                }
                r={4}
              />

              <text
                x={
                  w *
                  (0.24 +
                    i *
                      0.26)
                }
                y={h * 0.7}
                textAnchor="middle"
                fill="#ffffff"
                fontSize={6}
                fontWeight={800}
              >
                {name}
              </text>
            </g>
          ))}

          <text
            x={w / 2}
            y={h * 0.88}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6.2}
            fontWeight={800}
          >
            {spec.name}
          </text>
        </>
      );
    }

    /* =====================================================
       TERMINAL BLOCK
       ===================================================== */

    case "tb":
    case "tb_n":
    case "tb_pe": {
      const color =
        spec.body ===
        "tb_n"
          ? "var(--wire-neutral)"
          : spec.body ===
              "tb_pe"
            ? "var(--wire-earth)"
            : "url(#gGrey)";

      const n =
        spec.terminals.filter(
          (t) =>
            t.side ===
            "top"
        ).length || 4;

      return (
        <>
          <rect
            x={3}
            y={4}
            width={w}
            height={h}
            rx={4}
            fill="#000"
            opacity={0.23}
          />

          <rect
            x={0}
            y={0}
            width={w}
            height={h}
            rx={4}
            fill={color}
            stroke="#555d63"
          />

          {Array.from({
            length:
              n - 1,
          }).map((_, i) => (
            <line
              key={i}
              x1={
                ((i + 1) *
                  w) /
                n
              }
              y1={3}
              x2={
                ((i + 1) *
                  w) /
                n
              }
              y2={h - 3}
              stroke="#000"
              strokeOpacity={0.23}
            />
          ))}

          <rect
            x={0}
            y={h - 5}
            width={w}
            height={5}
            rx={2}
            fill="#000"
            opacity={0.18}
          />

          <text
            x={w / 2}
            y={h - 9}
            textAnchor="middle"
            fill={
              spec.body ===
              "tb_n"
                ? "#ffffff"
                : "#222"
            }
            fontSize={5.8}
            fontWeight={900}
          >
            {spec.body ===
            "tb_n"
              ? "N"
              : spec.body ===
                  "tb_pe"
                ? "PE"
                : "TERMINAL"}
          </text>
        </>
      );
    }

    /* =====================================================
       BUSBAR
       ===================================================== */

    case "busbar":
      return (
        <>
          <rect
            x={3}
            y={4}
            width={w}
            height={h}
            rx={3}
            fill="#000"
            opacity={0.24}
          />

          <polygon
            points={`0,5 7,0 ${w},0 ${w - 7},5`}
            fill="#e8a66a"
          />

          <polygon
            points={`${w - 7},5 ${w},0 ${w},${h - 5} ${w - 7},${h}`}
            fill="#75401f"
          />

          <rect
            x={0}
            y={5}
            width={w - 7}
            height={h - 5}
            rx={3}
            fill="#b96d32"
            stroke="#6f3b1a"
          />

          <rect
            x={1}
            y={6}
            width={w - 9}
            height={h * 0.22}
            rx={2}
            fill="#fff"
            opacity={0.2}
          />
        </>
      );

    /* =====================================================
       SOURCES
       ===================================================== */

    case "src3":
    case "src1":
    case "srcc":
      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            ribs={3}
          />

          <rect
            x={12}
            y={12}
            width={w - 24}
            height={h * 0.34}
            rx={3}
            fill="#06150d"
            stroke="#111"
          />

          <text
            x={w / 2}
            y={h * 0.3}
            className="dev-screen"
            textAnchor="middle"
          >
            {spec.body ===
            "src3"
              ? `3~ ${Math.round(comp.state.sourceVoltage ?? spec.sourceVoltage ?? 400)}V`
              : spec.body ===
                  "src1"
                ? `1~ ${Math.round(comp.state.sourceVoltage ?? spec.sourceVoltage ?? 230)}V`
                : comp.type ===
                    "src_ctrl_24"
                  ? "24V DC"
                  : "230V AC"}
          </text>

          <Led
            x={20}
            y={h * 0.63}
            on={monitor}
            r={4.5}
          />

          <text
            x={w / 2}
            y={h * 0.67}
            textAnchor="middle"
            fill="#fff"
            fontSize={6}
            fontWeight={800}
          >
            POWER
          </text>
        </>
      );

    /* =====================================================
       MOTOR
       ===================================================== */

    case "motor3":
    case "motor1": {
      const spinning =
        monitor &&
        running;

      const isThreePhase =
        spec.body === "motor3";

      const direction =
        motorDirection ?? "forward";

      const reverse =
        isThreePhase &&
        direction === "reverse";

      const directionArrow =
        reverse ? "↺" : "↻";

      const directionLabel =
        reverse ? "REV" : "FWD";

      return (
        <>
          <ellipse
            cx={w / 2}
            cy={h * 0.79}
            rx={w * 0.42}
            ry={h * 0.07}
            fill="#000"
            opacity={0.25}
          />

          <rect
            x={w * 0.28}
            y={h * 0.06}
            width={w * 0.44}
            height={h * 0.18}
            rx={4}
            fill="url(#gDark)"
            stroke="#263d48"
          />

          <rect
            x={w * 0.1}
            y={h * 0.22}
            width={w * 0.8}
            height={h * 0.56}
            rx={12}
            fill="url(#gMotor)"
            stroke="#294553"
          />

          {Array.from({
            length: 9,
          }).map((_, i) => (
            <line
              key={i}
              x1={
                w * 0.12 +
                (i *
                  w *
                  0.76) /
                  8
              }
              y1={h * 0.24}
              x2={
                w * 0.12 +
                (i *
                  w *
                  0.76) /
                  8
              }
              y2={h * 0.75}
              stroke="#173543"
              strokeWidth={2}
              opacity={0.5}
            />
          ))}

          <rect
            x={w * 0.04}
            y={h * 0.32}
            width={w * 0.1}
            height={h * 0.36}
            rx={5}
            fill="url(#gMotor)"
          />

          <g
            className={
              spinning
                ? "spin"
                : ""
            }
            style={{
              transformOrigin:
                `${w * 0.09}px ${h * 0.5}px`,
              animationDirection:
                reverse
                  ? "reverse"
                  : "normal",
            }}
          >
            <circle
              cx={w * 0.09}
              cy={h * 0.5}
              r={h * 0.09}
              fill={metal}
            />

            <line
              x1={
                w * 0.09 -
                h * 0.07
              }
              y1={h * 0.5}
              x2={
                w * 0.09 +
                h * 0.07
              }
              y2={h * 0.5}
              stroke="#4d555b"
              strokeWidth={3}
            />
          </g>

          {isThreePhase &&
            monitor &&
            running && (
              <g pointerEvents="none">
                {/* اتجاه الدوران داخل جسم المحرك */}
                <circle
                  cx={w * 0.76}
                  cy={h * 0.5}
                  r={h * 0.11}
                  fill="#071016"
                  opacity={0.7}
                  stroke={
                    reverse
                      ? "#ff8b55"
                      : "#58d48b"
                  }
                  strokeWidth={1.4}
                />

                <text
                  x={w * 0.76}
                  y={h * 0.5 + 5}
                  textAnchor="middle"
                  fill={
                    reverse
                      ? "#ff9a6a"
                      : "#69e39a"
                  }
                  fontSize={18}
                  fontWeight={900}
                  style={{
                    paintOrder: "stroke",
                    stroke: "#071016",
                    strokeWidth: 2.2,
                  }}
                >
                  {directionArrow}
                </text>

                <rect
                  x={w * 0.69}
                  y={h * 0.61}
                  width={w * 0.14}
                  height={h * 0.085}
                  rx={2.5}
                  fill="#10171c"
                  stroke={
                    reverse
                      ? "#ff8b55"
                      : "#58d48b"
                  }
                  strokeWidth={0.9}
                />

                <text
                  x={w * 0.76}
                  y={h * 0.67}
                  textAnchor="middle"
                  fill="#ffffff"
                  fontSize={6}
                  fontWeight={900}
                >
                  {directionLabel}
                </text>
              </g>
            )}

          <rect
            x={w * 0.16}
            y={h * 0.76}
            width={w * 0.68}
            height={h * 0.08}
            rx={2}
            fill="#273c46"
          />

          <rect
            x={w * 0.34}
            y={h * 0.4}
            width={w * 0.32}
            height={h * 0.2}
            rx={2}
            fill={metal}
            stroke="#555d63"
          />

          <text
            x={w / 2}
            y={h * 0.49}
            textAnchor="middle"
            fill="#222"
            fontSize={6}
            fontWeight={900}
          >
            {spec.body ===
            "motor3"
              ? "M 3~"
              : "M 1~"}
          </text>

          <text
            x={w / 2}
            y={h * 0.57}
            textAnchor="middle"
            fill="#222"
            fontSize={5.5}
            fontWeight={700}
          >
            {spec.body ===
            "motor3"
              ? "400V"
              : "230V"}
          </text>

          <text
            x={w / 2}
            y={h * 0.71}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={6}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#23404d",
              strokeWidth: 1,
            }}
          >
            {monitor
              ? running
                ? "RUNNING"
                : "STOPPED"
              : ""}
          </text>
        </>
      );
    }

    /* =====================================================
       PILOT LAMP
       ومن ضمنها الأزرق
       ===================================================== */

    case "lamp": {
      const type =
        String(spec.type);

      const lit =
        monitor &&
        (running ||
          energized);

      const cy =
        h * 0.42;

      const color =
        type.includes(
          "blue"
        )
          ? "#2788ff"
          : type.includes(
                "red"
              )
            ? "#e53c35"
            : type.includes(
                  "green"
                )
              ? "#32bd68"
              : type.includes(
                    "amber"
                  ) ||
                  type.includes(
                    "yellow"
                  )
                ? "#f3b735"
                : "#f0cb42";

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            r={9}
          />

          <circle
            cx={w / 2}
            cy={cy}
            r={w * 0.34}
            fill={metal}
            stroke="#5c646a"
          />

          {lit && (
            <>
              <circle
                cx={w / 2}
                cy={cy}
                r={w * 0.58}
                fill={color}
                opacity={0.18}
              />

              <circle
                cx={w / 2}
                cy={cy}
                r={w * 0.43}
                fill={color}
                opacity={0.27}
              />
            </>
          )}

          <circle
            cx={w / 2}
            cy={cy}
            r={w * 0.27}
            fill={
              lit
                ? color
                : "#454c51"
            }
            stroke="#202529"
            strokeWidth={1.2}
          />

          {[
            0,
            45,
            90,
            135,
          ].map((a) => (
            <line
              key={a}
              x1={
                w / 2 -
                w * 0.25
              }
              y1={cy}
              x2={
                w / 2 +
                w * 0.25
              }
              y2={cy}
              stroke="#000"
              strokeOpacity={0.13}
              transform={`rotate(${a} ${w / 2} ${cy})`}
            />
          ))}

          <ellipse
            cx={
              w / 2 -
              w * 0.07
            }
            cy={
              cy -
              w * 0.09
            }
            rx={w * 0.1}
            ry={w * 0.05}
            fill="#fff"
            opacity={
              lit ? 0.7 : 0.2
            }
          />
        </>
      );
    }

    /* =====================================================
       FAN
       ===================================================== */

    case "fan": {
      const spinning =
        monitor &&
        running;

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            r={10}
          />

          <circle
            cx={w / 2}
            cy={h / 2}
            r={w * 0.42}
            fill="#0e1215"
            stroke="#626a70"
          />

          <g
            className={
              spinning
                ? "spin-fast"
                : ""
            }
            style={{
              transformOrigin:
                `${w / 2}px ${h / 2}px`,
              animationDirection:
                motorDirection === "reverse"
                  ? "reverse"
                  : "normal",
            }}
          >
            {[
              0,
              72,
              144,
              216,
              288,
            ].map((a) => (
              <path
                key={a}
                d={`M ${w / 2} ${h / 2} q ${w * 0.16} ${-w * 0.16} ${w * 0.05} ${-w * 0.36} q ${-w * 0.22} ${w * 0.1} ${-w * 0.05} ${w * 0.36} z`}
                fill="url(#gGrey)"
                transform={`rotate(${a} ${w / 2} ${h / 2})`}
              />
            ))}
          </g>

          <circle
            cx={w / 2}
            cy={h / 2}
            r={w * 0.1}
            fill={metal}
          />

          {[
            0.18,
            0.28,
            0.38,
          ].map((r) => (
            <circle
              key={r}
              cx={w / 2}
              cy={h / 2}
              r={w * r}
              fill="none"
              stroke="#fff"
              strokeOpacity={0.12}
            />
          ))}

          {comp.type === "fan_dc" && (
            <text
              x={w / 2}
              y={h - 9}
              textAnchor="middle"
              fontSize={12}
              fontWeight={800}
              fill="#e8f4ff"
            >
              FAN DC
            </text>
          )}
        </>
      );
    }

    /* =====================================================
       AIR CONDITION
       ===================================================== */

    case "ac": {
      const cooling =
        monitor &&
        running;

      return (
        <>
          <rect
            x={3}
            y={4}
            width={w}
            height={h * 0.78}
            rx={14}
            fill="#000"
            opacity={0.23}
          />

          <rect
            x={0}
            y={0}
            width={w}
            height={h * 0.78}
            rx={14}
            fill="url(#gWhite)"
            stroke="#b5b9b7"
          />

          {Array.from({
            length: 5,
          }).map((_, i) => (
            <line
              key={i}
              x1={w * 0.08}
              y1={
                h * 0.16 +
                i * 5
              }
              x2={w * 0.92}
              y2={
                h * 0.16 +
                i * 5
              }
              stroke="#808886"
              strokeOpacity={0.22}
            />
          ))}

          <rect
            x={w * 0.08}
            y={h * 0.58}
            width={w * 0.84}
            height={h * 0.12}
            rx={6}
            fill={metal}
            stroke="#a5aba8"
          />

          {cooling &&
            [
              0.25,
              0.5,
              0.75,
            ].map((p) => (
              <path
                key={p}
                d={`M ${w * p} ${h * 0.72} q 8 10 0 20`}
                fill="none"
                stroke="#58ade0"
                strokeWidth={2}
                opacity={0.6}
              />
            ))}

          <Led
            x={w * 0.86}
            y={h * 0.42}
            on={cooling}
            r={4}
          />
        </>
      );
    }

    /* =====================================================
       SOLENOID
       ===================================================== */

    case "solenoid": {
      const active =
        monitor &&
        running;

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gDark)"
            r={8}
          />

          <rect
            x={w * 0.18}
            y={h * 0.16}
            width={w * 0.44}
            height={h * 0.46}
            rx={4}
            fill="#15191c"
            stroke="#080a0c"
          />

          {[
            0,
            1,
            2,
            3,
            4,
          ].map((i) => (
            <line
              key={i}
              x1={w * 0.2}
              y1={
                h *
                (0.22 +
                  i *
                    0.09)
              }
              x2={w * 0.6}
              y2={
                h *
                (0.22 +
                  i *
                    0.09)
              }
              stroke={
                active
                  ? "#eca044"
                  : "#a16e37"
              }
              strokeWidth={3}
            />
          ))}

          <rect
            x={w * 0.62}
            y={h * 0.3}
            width={w * 0.3}
            height={h * 0.3}
            rx={3}
            fill={metal}
            stroke="#555c62"
          />

          <rect
            x={
              active
                ? w * 0.66
                : w * 0.6
            }
            y={h * 0.38}
            width={w * 0.14}
            height={h * 0.14}
            rx={2}
            fill="#737c82"
            style={{
              transition:
                "x 120ms ease-out",
            }}
          />

          <Led
            x={w * 0.86}
            y={h * 0.2}
            on={active}
            r={4}
          />
        </>
      );
    }

    /* =====================================================
       HEATER
       ===================================================== */

    case "heater": {
      const hot =
        monitor &&
        running;

      return (
        <>
          <Case
            w={w}
            h={h}
            fill="url(#gGrey)"
            r={7}
          />

          <rect
            x={w * 0.08}
            y={h * 0.18}
            width={w * 0.84}
            height={h * 0.46}
            rx={4}
            fill="#1b2026"
          />

          {[
            0.28,
            0.42,
            0.56,
          ].map((y, i) => (
            <path
              key={i}
              d={`M ${w * 0.12} ${h * y} q ${w * 0.1} -10 ${w * 0.2} 0 q ${w * 0.1} 10 ${w * 0.2} 0 q ${w * 0.1} -10 ${w * 0.2} 0 q ${w * 0.1} 10 ${w * 0.2} 0`}
              fill="none"
              stroke={
                hot
                  ? "#ff693e"
                  : "#848d93"
              }
              strokeWidth={3.5}
              strokeLinecap="round"
            />
          ))}

          {hot && (
            <rect
              x={w * 0.08}
              y={h * 0.18}
              width={w * 0.84}
              height={h * 0.46}
              rx={4}
              fill="#ff693e"
              opacity={0.15}
            />
          )}

          <text
            x={w / 2}
            y={h * 0.85}
            textAnchor="middle"
            fill="#ffffff"
            fontSize={7}
            fontWeight={900}
            style={{
              paintOrder: "stroke",
              stroke: "#111",
              strokeWidth: 1.5,
            }}
          >
            {hot
              ? "HEATER ON"
              : "HEATER OFF"}
          </text>
        </>
      );
    }

    default:
      return (
        <Case
          w={w}
          h={h}
        />
      );
  }
}

/* =========================================================
   TERMINAL LABEL LOGIC
   ========================================================= */

type DeviceTerminal =
  DeviceSpec["terminals"][number];

function getTerminalNumber(
  t: DeviceTerminal
) {
  const raw =
    `${t.label ?? ""} ${t.id ?? ""}`;

  const match =
    raw.match(
      /(?:^|\D)(\d{2})(?:\D|$)/
    );

  return (
    match?.[1] ??
    ""
  );
}

function terminalDisplayLabel(
  spec: DeviceSpec,
  compType: string,
  t: DeviceTerminal
): {
  main: string;
  sub: string;
} {
  const raw =
    String(
      t.label ||
        t.id ||
        ""
    ).trim();

  if (!raw) {
    return {
      main: "",
      sub: "",
    };
  }

  /* إذا التسمية نفسها بيها NO/NC
     نخلي الرقم وحده فوق ونوع النقطة جوه */
  const clean =
    raw
      .replace(
        /\b(NO|NC|COM)\b/gi,
        ""
      )
      .replace(
        /\s+/g,
        " "
      )
      .trim();

  let sub = "";

  if (
    /\bNO\b/i.test(raw)
  ) {
    sub = "NO";
  }

  if (
    /\bNC\b/i.test(raw)
  ) {
    sub = "NC";
  }

  if (
    /\bCOM\b/i.test(raw)
  ) {
    sub = "COM";
  }

  const code =
    getTerminalNumber(t);

  /* -------------------------
     CONTACTOR
     ------------------------- */

  if (
    spec.body ===
    "contactor"
  ) {
    if (
      [
        "13",
        "14",
        "43",
        "44",
        "53",
        "54",
      ].includes(code)
    ) {
      sub = "NO";
    }

    if (
      [
        "21",
        "22",
        "31",
        "32",
        "61",
        "62",
      ].includes(code)
    ) {
      sub = "NC";
    }
  }

  /* -------------------------
     RELAY / TIMER / AUX
     ------------------------- */

  if (
    spec.body ===
      "relay" ||
    spec.body ===
      "timer" ||
    spec.body ===
      "aux" ||
    spec.body ===
      "limit"
  ) {
    /*
      IEC typical:
      11 COM
      12 NC
      14 NO
      21 COM
      22 NC
      24 NO
    */

    if (
      code.endsWith(
        "1"
      )
    ) {
      sub = "COM";
    }

    if (
      code.endsWith(
        "2"
      )
    ) {
      sub = "NC";
    }

    if (
      code.endsWith(
        "4"
      )
    ) {
      sub = "NO";
    }
  }

  /* -------------------------
     OVERLOAD
     ------------------------- */

  if (
    spec.body ===
    "overload"
  ) {
    if (
      code === "95" ||
      code === "96"
    ) {
      sub = "NC";
    }

    if (
      code === "97" ||
      code === "98"
    ) {
      sub = "NO";
    }
  }

  /* -------------------------
     ESTOP
     ------------------------- */

  if (
    spec.body ===
    "estop"
  ) {
    sub = "NC";
  }

  /* -------------------------
     PUSH BUTTON
     ------------------------- */

  if (
    spec.body ===
      "pb_green" ||
    spec.body ===
      "pb_red" ||
    spec.body ===
      "pb_black"
  ) {
    if (
      compType ===
        "pb_stop" ||
      compType ===
        "pb_nc"
    ) {
      sub = "NC";
    }

    if (
      compType ===
        "pb_start" ||
      compType ===
        "pb_no"
    ) {
      sub = "NO";
    }

    if (
      compType ===
      "pb_no_nc"
    ) {
      if (
        clean === "1" ||
        clean === "2"
      ) {
        sub = "NC";
      }

      if (
        clean === "3" ||
        clean === "4"
      ) {
        sub = "NO";
      }
    }
  }

  return {
    main:
      clean || raw,
    sub,
  };
}

/* =========================================================
   TERMINAL TEXT
   ========================================================= */

function TerminalText({
  x,
  y,
  main,
  sub,
  side,
}: {
  x: number;
  y: number;
  main: string;
  sub: string;
  side: string;
}) {
  const horizontal =
    side === "left" ||
    side === "right";

  const anchor =
    side === "left"
      ? "end"
      : side === "right"
        ? "start"
        : "middle";

  return (
    <g
      pointerEvents="none"
    >
      {/* اسم الطرف الرئيسي مثل L1 / A1 / 13 */}
      <text
        x={x}
        y={y}
        textAnchor={anchor}
        dominantBaseline={
          horizontal
            ? "middle"
            : undefined
        }
        fill="#111827"
        fontSize={9.5}
        fontWeight={900}
        style={{
          paintOrder:
            "stroke",
          stroke: "#ffffff",
          strokeWidth: 3,
          strokeLinejoin:
            "round",
        }}
      >
        {main}
      </text>

      {/* NO / NC / COM أبيض */}
      {sub && (
        <text
          x={x}
          y={
            horizontal
              ? y + 11
              : side ===
                  "top"
                ? y - 10
                : y + 11
          }
          textAnchor={anchor}
          dominantBaseline={
            horizontal
              ? "middle"
              : undefined
          }
          fill="#ffffff"
          fontSize={7}
          fontWeight={900}
          letterSpacing={0.2}
          style={{
            paintOrder:
              "stroke",
            stroke: "#161b20",
            strokeWidth: 2.5,
            strokeLinejoin:
              "round",
          }}
        >
          {sub}
        </text>
      )}
    </g>
  );
}

/* =========================================================
   DEVICE VIEW
   ========================================================= */

function DeviceViewInner(
  props: DeviceViewProps
) {
  const {
    comp,
    selected,
    monitor,
    liveTerminals,
    wiredTerminals,
    pendingTerminal,
  } = props;

  const spec =
    SPECS[comp.type];

  if (!spec) {
    return null;
  }

  return (
    <g
      transform={`translate(${comp.x} ${comp.y}) rotate(${comp.rot} ${spec.w / 2} ${spec.h / 2})`}
      className="device"
    >
      {/* تحديد العنصر */}
      {selected && (
        <rect
          x={-12}
          y={-12}
          width={
            spec.w + 24
          }
          height={
            spec.h + 24
          }
          rx={11}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth={2}
          strokeDasharray="6 5"
        />
      )}

      {/* جسم الجهاز */}
      <g
        onPointerDown={(
          e
        ) => {
          props.onBodyPointerDown(
            e,
            comp.id
          );

          props.onDeviceAction(
            e,
            comp.id,
            "down"
          );
        }}
        onPointerUp={(
          e
        ) =>
          props.onDeviceAction(
            e,
            comp.id,
            "up"
          )
        }
        onPointerCancel={(
          e
        ) =>
          props.onDeviceAction(
            e,
            comp.id,
            "up"
          )
        }
        onPointerLeave={(
          e
        ) =>
          props.onDeviceAction(
            e,
            comp.id,
            "up"
          )
        }
        style={{
          cursor:
            monitor
              ? "pointer"
              : "grab",
        }}
      >
        <Body
          spec={spec}
          comp={comp}
          monitor={
            monitor
          }
          energized={
            props.energized
          }
          coilPowered={
            props.coilPowered
          }
          running={
            props.running
          }
          motorDirection={
            props.motorDirection
          }
          linkLabel={
            props.linkLabel ??
            null
          }
          {...(props.actuated !==
          undefined
            ? {
                actuated:
                  props.actuated,
              }
            : {})}
        />
      </g>

      {/* أزرار الأوفرلود الحقيقية: STOP يفصل، RESET يعيد الضبط.
          تعمل في وضع التشغيل فقط ولا تمنع تحريك القطعة أثناء التحرير. */}
      {spec.type === "overload" && monitor && (
        <g>
          <circle
            cx={144.8}
            cy={117.2}
            r={15}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onPointerDown={(e) => {
              e.stopPropagation();
              props.onOverloadControl(e, comp.id, "stop");
            }}
          />
          <circle
            cx={145.4}
            cy={70.1}
            r={15}
            fill="transparent"
            style={{ cursor: "pointer" }}
            onPointerDown={(e) => {
              e.stopPropagation();
              props.onOverloadControl(e, comp.id, "reset");
            }}
          />
        </g>
      )}

      {/* اسم العنصر KM1 / K1 / Q1 */}
      <text
        x={spec.w / 2}
        y={-37}
        textAnchor="middle"
        fill="#ea7215"
        fontSize={12}
        fontWeight={900}
        style={{
          paintOrder:
            "stroke",
          stroke: "#ffffff",
          strokeWidth: 3.5,
          strokeLinejoin:
            "round",
        }}
        pointerEvents="none"
      >
        {comp.label}
      </text>

      {/* ===================================================
          TERMINALS
          كل طرف = برغي واحد + كتابة واحدة
          =================================================== */}

      {spec.terminals.map(
        (t) => {
          const p =
            terminalLocal(
              spec,
              t
            );

          const live =
            liveTerminals.has(
              `${comp.id}:${t.id}`
            );

          const pending =
            pendingTerminal ===
            `${comp.id}:${t.id}`;

          const wired =
            wiredTerminals.has(
              `${comp.id}:${t.id}`
            );

          const outward =
            t.side ===
            "top"
              ? {
                  x: 0,
                  y: -1,
                }
              : t.side ===
                  "bottom"
                ? {
                    x: 0,
                    y: 1,
                  }
                : t.side ===
                    "left"
                  ? {
                      x: -1,
                      y: 0,
                    }
                  : {
                      x: 1,
                      y: 0,
                    };

          /*
            البرغي قريب من جسم الجهاز.
            لا يوجد أي برغي ثاني.
          */

          /*
            ضبط نقاط الريليه رقم رقم.

            غيّر فقط قيم x و y للرقم الذي تريد تحريكه:
            x موجب = يمين
            x سالب  = يسار
            y موجب = جوه
            y سالب  = فوك

            الدائرة البرتقالية + منطقة الضغط + نقطة خروج السلك
            كلها تستخدم نفس tx / ty، لذلك تتحرك معًا 100%.
          */
          const relayOffset: Record<string, { x: number; y: number }> = {
            "1":  { x: -10, y: -1 },
            "2":  { x: -7.5, y: -1 },
            "3":  { x: -5.5, y: -1 },
            "4":  { x: -3, y:-4 },
            "5":  { x: -9.5, y: -1},
            "6":  { x: -7, y: -1 },
            "7":  { x: -5.3, y: -1 },
            "8":  { x: -3.5, y: -1.5 },
            "9":  { x: -9.5, y:-4 },
            "10": { x: -7.5, y:-4 },
            "11": { x: -5, y:-4 },
            "12": { x: -2.5, y:-3.5 },
            "13": { x: -7, y:-4.5 },
            "14": { x: -5, y:-4.5 },
          };

          const relayOff =
            relayOffset[String(t.id)] ?? { x: 0, y: 0 };

          /*
            ضبط نقاط الكونتكتر الثلاثي LC1D25 نقطة نقطة فقط.

            مهم جدًا:
            - هذا الجدول يشتغل فقط عندما spec.type === "contactor".
            - الكونتكتر الرباعي contactor_small لا يتأثر نهائيًا.
            - أسماء الأطراف هنا مطابقة لتعريف الكونتكتر الثلاثي في catalog.ts:
              L1 L2 L3 / T1 T2 T3 / A1 A2 / 13 14 / 21 22

            التحريك:
            x موجب = يمين
            x سالب  = يسار
            y موجب = جوه
            y سالب  = فوك

            مثال:
            "L1": { x: 3, y: -2 }
            يعني نقطة L1 تتحرك 3 يمين و2 فوك.

            الدائرة + منطقة الضغط + نقطة خروج السلك
            كلها تعتمد نفس tx / ty وتتحرك سوا.
          */
          const contactorOffset: Record<string, { x: number; y: number }> = {
            "L1": { x: 0, y: 0 },
            "L2": { x: 0, y: 0 },
            "L3": { x: 0, y: 0 },

            "T1": { x: 0, y: 0 },
            "T2": { x: 0, y: 0 },
            "T3": { x: 0, y: 0 },

            "A1": { x: 0, y: 0 },
            "A2": { x: 0, y: 0 },

            "13": { x: 0, y: 0 },
            "14": { x: 0, y: 0 },
            "21": { x: 0, y: 0 },
            "22": { x: 0, y: 0 },
          };

          const contactorOff =
            spec.type === "contactor"
              ? contactorOffset[String(t.id)] ?? { x: 0, y: 0 }
              : { x: 0, y: 0 };

          // الريليه يحتفظ بضبطه الحالي.
          // الكونتكتر الثلاثي فقط يأخذ إزاحة مستقلة لكل نقطة فوق مكانه الحالي.
          // باقي الأجهزة، ومنها الكونتكتر الرباعي، تبقى كما كانت تمامًا.
          const isPF083Timer =
            spec.type === "timer" || spec.type === "timer_off";

          const isProtectionImage =
            spec.type === "phase_failure" ||
            spec.type === "voltage_monitor";

          const tx =
            spec.type === "relay"
              ? p.x + relayOff.x
              : isPF083Timer
                ? p.x
                : isProtectionImage
                  ? p.x
                  : spec.type === "contactor"
                  ? p.x + outward.x * 6 + contactorOff.x
                  : p.x + outward.x * 6;

          const ty =
            spec.type === "relay"
              ? p.y + relayOff.y
              : isPF083Timer
                ? p.y
                : isProtectionImage
                  ? p.y
                  : spec.type === "contactor"
                  ? p.y + outward.y * 6 + contactorOff.y
                  : p.y + outward.y * 6;

          /*
            الكتابة أبعد من البرغي حتى لا تتداخل.
          */

          let textX =
            p.x;

          let textY =
            p.y;

          if (
            t.side ===
            "top"
          ) {
            textX = p.x;
            textY =
              p.y - 26;
          }

          if (
            t.side ===
            "bottom"
          ) {
            textX = p.x;
            textY =
              p.y + 34;
          }

          if (
            t.side ===
            "left"
          ) {
            textX =
              p.x - 30;
            textY = p.y;
          }

          if (
            t.side ===
            "right"
          ) {
            textX =
              p.x + 30;
            textY = p.y;
          }

          const label =
            terminalDisplayLabel(
              spec,
              String(
                comp.type
              ),
              t
            );

          return (
            <g key={t.id}>
              {/* صورة LC1D25 تبقي البرغي الأصلي ظاهرًا؛ حلقة الربط فقط
                  توضع فوق مركزه. باقي القطع تبقى على البرغي القديم. */}
              {spec.type === "relay" ? null : (isPF083Timer || isProtectionImage) ? (
                <g pointerEvents="none">
                  {monitor && live && (
                    <circle
                      cx={tx}
                      cy={ty}
                      r={isProtectionImage ? 14: 9.6}
                      fill="#37f56a"
                      opacity={0.22}
                    />
                  )}
                  <circle
                    cx={tx}
                    cy={ty}
                    r={isProtectionImage ? 7 : 3}
                    fill={wired ? "#22c55e" : "none"}
                    fillOpacity={wired ? 0.88 : 0}
                    stroke={monitor && live ? "#37f56a" : "#22c55e"}
                    strokeWidth={wired ? 1.2 : 1.35}
                    opacity={wired || (monitor && live) ? 1 : 0.78}
                  />
                </g>
              ) : spec.type === "overload" ? (
                <g pointerEvents="none">
                  {monitor && live && (
                    <circle cx={tx} cy={ty} r={9.8} fill="#37f56a" opacity={0.22} />
                  )}
                  <circle
                    cx={tx}
                    cy={ty}
                    r={7.8}
                    fill={wired ? "#22c55e" : "none"}
                    fillOpacity={wired ? 0.88 : 0}
                    stroke={monitor && live ? "#37f56a" : "#22c55e"}
                    strokeWidth={1.35}
                    opacity={wired || (monitor && live) ? 1 : 0.82}
                  />
                </g>
              ) : (spec.type === "contactor" || spec.type === "contactor_small" || spec.type === "aux_block") ? (
                <g pointerEvents="none">
                  {/* الحلقة أصغر ومتمركزة على قلب البرغي نفسه. عند توصيل
                      سلك تصبح النقطة دائرة خضراء كاملة، وعند وجود فولت
                      نضيف هالة خفيفة بدون تغيير مركز البرغي. */}
                  {monitor && live && (
                    <circle cx={tx} cy={ty} r={10.2} fill="#37f56a" opacity={0.22} />
                  )}
                  <circle
                    cx={tx}
                    cy={ty}
                    r={8.1}
                    fill={wired ? "#22c55e" : "none"}
                    fillOpacity={wired ? 0.88 : 0}
                    stroke={monitor && live ? "#37f56a" : "#22c55e"}
                    strokeWidth={wired ? 1.2 : 1.35}
                    opacity={wired || (monitor && live) ? 1 : 0.78}
                  />
                </g>
              ) : (
                <TerminalWell
                  x={tx}
                  y={ty}
                  side={
                    t.side
                  }
                  live={
                    monitor &&
                    live
                  }
                />
              )}

              {/* تحديد نقطة التوصيل */}
              {pending && (spec.type === "relay" ? (
                <circle
                  cx={tx}
                  cy={ty}
                  r={9.5}
                  fill="rgba(234,114,21,0.08)"
                  stroke="#ea7215"
                  strokeWidth={2.2}
                  pointerEvents="none"
                />
              ) : (
                <circle
                  cx={tx}
                  cy={ty}
                  r={isProtectionImage ? 9 : 4}
                  fill="none"
                  stroke="var(--color-primary)"
                  strokeWidth={2.5}
                />
              ))}

              {/* تسميات LC1D25 مطبوعة أصلًا على الصورة، فلا نكررها فوقها. */}
              {spec.type === "relay" ? null : !isPF083Timer && !isProtectionImage && spec.type !== "contactor" && spec.type !== "contactor_small" && spec.type !== "overload" && spec.type !== "aux_block" && (
                <TerminalText
                  x={textX}
                  y={textY}
                  main={label.main}
                  sub={label.sub}
                  side={t.side}
                />
              )}

              {/* منطقة الضغط للتوصيل */}
              <circle
                cx={tx}
                cy={ty}
                r={
  isProtectionImage
    ? 14
    : spec.type === "relay"
      ? 11
      : 18
}
                fill="transparent"
                className="term-hit"
                data-term={`${comp.label}:${t.id}`}
                onPointerDown={(
                  e
                ) =>
                  props.onTerminalPointerDown(
                    e,
                    comp.id,
                    t.id
                  )
                }
              />
            </g>
          );
        }
      )}
    </g>
  );
}

export const DeviceView =
  memo(DeviceViewInner);